import type { EmbedPosition, ResizePayload, ViewportPayload } from './types'

/**
 * Safe initial size before the iframe reports its real collapsed geometry —
 * large enough to hold the launcher bubble + its shadow so nothing is clipped
 * on first paint, small enough that the rest of the host page stays clickable.
 */
export const INITIAL_COLLAPSED = { width: 120, height: 120 }

const BASE_STYLE: Partial<CSSStyleDeclaration> = {
  position: 'fixed',
  bottom: '0px',
  border: '0',
  margin: '0',
  padding: '0',
  // High enough to sit above typical host chrome without an arms race.
  zIndex: '2147483000',
  colorScheme: 'normal',
  background: 'transparent',
  // The iframe is only ever sized to the visible surface (bubble when
  // collapsed, panel when expanded), so it never covers — and never eats
  // host-page clicks over — anything but the widget itself.
  pointerEvents: 'auto',
  maxWidth: '100vw',
  maxHeight: '100vh',
}

/** Apply the one-time base styling + corner anchoring to the iframe element. */
export function styleFrame(
  iframe: HTMLIFrameElement,
  position: EmbedPosition,
): void {
  Object.assign(iframe.style, BASE_STYLE)
  // Anchor one side; leave the other unset (CSS initial `auto`) so the frame
  // hugs the chosen corner.
  if (position === 'bottom-left') {
    iframe.style.left = '0px'
    iframe.style.right = ''
  } else {
    iframe.style.right = '0px'
    iframe.style.left = ''
  }
  iframe.style.width = `${INITIAL_COLLAPSED.width}px`
  iframe.style.height = `${INITIAL_COLLAPSED.height}px`
  iframe.setAttribute('aria-hidden', 'false')
}

function clampPixels(value: number, fallback: number): number {
  if (typeof value !== 'number' || !Number.isFinite(value) || value <= 0) {
    return fallback
  }
  return Math.ceil(value)
}

/**
 * Resize the iframe to the geometry the widget requested, clamped to the host
 * viewport so an expanded panel can never exceed the visible page (the loader
 * knows the true host viewport; the iframe does not). Invalid dimensions fall
 * back to the initial collapsed box rather than throwing.
 */
export function applyResize(
  iframe: HTMLIFrameElement,
  payload: ResizePayload,
  viewport?: Pick<ViewportPayload, 'width' | 'height'>,
): void {
  let width = clampPixels(payload.width, INITIAL_COLLAPSED.width)
  let height = clampPixels(payload.height, INITIAL_COLLAPSED.height)
  if (viewport) {
    if (viewport.width > 0) width = Math.min(width, viewport.width)
    if (viewport.height > 0) height = Math.min(height, viewport.height)
  }
  iframe.style.width = `${width}px`
  iframe.style.height = `${height}px`
}
